import React, { useEffect, useState, useRef } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  Spinner,
  MessageBar,
  MessageBarType,
  DetailsList,
  DetailsListLayoutMode,
  Selection,
  SelectionMode,
  IColumn,
  CommandBar,
  ICommandBarItemProps,
  Icon,
  TooltipHost,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  TextField,
  Dropdown,
  IDropdownOption,
  Callout,
  DirectionalHint,
  Toggle,
  Label,
  Pivot,
  PivotItem,
} from '@fluentui/react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { IBestellungMitPositionen, IBestellposition, IKreditor, GenehmigungsStatus, BestellStatus, IUserPermissions } from '../../shared/models';
import { bestellungenApi, kreditorenApi, approvalFlowApi, lagerorteApi } from '../services/api';
import { BestellpositionFormular } from '../components/BestellpositionFormular';
import { ApprovalFlowPanel } from '../components/ApprovalFlowPanel';
import { DokumentePanel } from '../components/DokumentePanel';
import { StatusverlaufPanel } from '../components/StatusverlaufPanel';
import { KommentarSektion } from '../components/KommentarSektion';
import { KreditorAnlegenDialog } from '../components/KreditorAnlegenDialog';
import { useAuth } from '../context/AuthContext';
import { useHelp } from '../context/HelpContext';
import { TabBadge } from '../components/TabBadge';
import axios from 'axios';

const getGenehmigungsstatusColor = (status: string): string => {
  switch (status) {
    case 'Freigegeben':
      return '#107c10';
    case 'Teilweise freigegeben':
      return '#0078d4';
    case 'Offen':
      return '#ffaa44';
    case 'Abgelehnt':
      return '#d13438';
    case 's. Position':
      return '#0078d4';
    default:
      return '#605e5c';
  }
};

const getBestellstatusColor = (status: string): string => {
  switch (status) {
    case 'Geliefert':
      return '#107c10'; // Dunkelgrün
    case 'Teilweise geliefert':
      return '#5db85d'; // Hellgrün
    case 'Bestellt':
      return '#ffaa44'; // Orange
    case 'Eingereicht':
      return '#d13438'; // Rot
    case 's. Position':
      return '#0078d4'; // Blau
    default:
      return '#605e5c'; // Grau
  }
};

export const BestellungDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { helpMode } = useHelp();
  const { id } = useParams<{ id: string }>();
  const isArchived = location.pathname.startsWith('/archiv/');
  const [bestellung, setBestellung] = useState<IBestellungMitPositionen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPositions, setSelectedPositions] = useState<IBestellposition[]>([]);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteAllDialogVisible, setDeleteAllDialogVisible] = useState(false);
  const [positionFormOpen, setPositionFormOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<IBestellposition | undefined>(undefined);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [kreditoren, setKreditoren] = useState<IKreditor[]>([]);
  const [globalSettingsExpanded, setGlobalSettingsExpanded] = useState(false);
  const [approvalFlowPanelOpen, setApprovalFlowPanelOpen] = useState(false);
  const [approveAllDialogVisible, setApproveAllDialogVisible] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [approveAllComment, setApproveAllComment] = useState('');
  const [permissions, setPermissions] = useState<IUserPermissions | null>(null);
  const [showKreditorDialog, setShowKreditorDialog] = useState(false);
  const [kreditorSearchText, setKreditorSearchText] = useState('');
  const [showKreditorDropdown, setShowKreditorDropdown] = useState(false);
  const [includeBCKreditoren, setIncludeBCKreditoren] = useState(true);
  const [selectedKreditorName, setSelectedKreditorName] = useState('');
  const kreditorContainerRef = useRef<HTMLDivElement>(null);

  // Lokale States für alle Optionen-Felder (Speichern per onBlur/onChange, KEIN Reload danach)
  const [localBCBestellnummer, setLocalBCBestellnummer] = useState('');
  const [localLieferterminKW, setLocalLieferterminKW] = useState('');
  const [localKostenstelle, setLocalKostenstelle] = useState('');
  const [localSachkonto, setLocalSachkonto] = useState('');
  const [localInventar, setLocalInventar] = useState('');
  const [localGenehmigungsstatus, setLocalGenehmigungsstatus] = useState('');
  const [localBestellstatus, setLocalBestellstatus] = useState('');
  const [localLagerortCode, setLocalLagerortCode] = useState('BÖGRA');
  const [lagerorte, setLagerorte] = useState<{ Code: string; Name: string | null }[]>([]);
  const [archiveDialogVisible, setArchiveDialogVisible] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [unarchiveDialogVisible, setUnarchiveDialogVisible] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [bcSyncRunning, setBcSyncRunning] = useState(false);
  const [lieferungDialogVisible, setLieferungDialogVisible] = useState(false);
  const [lieferungGesamte, setLieferungGesamte] = useState(false);
  const [lieferungPositionen, setLieferungPositionen] = useState<Record<number, { checked: boolean; menge: string }>>({});
  const [lieferungSaving, setLieferungSaving] = useState(false);
  const [bcSyncResultDialog, setBcSyncResultDialog] = useState<{ visible: boolean; success: boolean; message: string; details?: string }>({ visible: false, success: false, message: '' });
  const [bcOpenDialog, setBcOpenDialog] = useState<{ visible: boolean; browserUrl: string; clientUrl: string }>({ visible: false, browserUrl: '', clientUrl: '' });
  const [bestellungBadges, setBestellungBadges] = useState<{
    kommentare: { total: number; unread: number; hasMention: boolean };
    dokumente:  { total: number; unread: number };
  } | null>(null);

  const selection = new Selection({
    onSelectionChanged: () => {
      setSelectedPositions(selection.getSelection() as IBestellposition[]);
    },
  });

  useEffect(() => {
    lagerorteApi.getAll().then(data => setLagerorte(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (id) {
        const bestellungId = parseInt(id, 10);
        await loadBestellung(bestellungId, isMounted);
        await loadPermissions(bestellungId, isMounted);
      }
      await loadKreditoren(isMounted);
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  // Synchronisiere lokale Optionen-States aus geladenen Positions-Daten
  const syncLocalStatesFromData = (data: IBestellungMitPositionen) => {
    const getAgg = (fieldName: keyof IBestellposition): string => {
      if (!data.Positionen || data.Positionen.length === 0) return '';
      const values = data.Positionen.map((p) => p[fieldName] as string).filter((v) => v);
      if (values.length === 0) return '';
      const first = values[0];
      return values.every((v) => v === first) ? first : 's. Position';
    };
    setLocalBCBestellnummer(getAgg('BCBestellnummer'));
    setLocalLieferterminKW(getAgg('LieferterminKW'));
    setLocalKostenstelle(getAgg('EmpfangendeKostenstelle'));
    setLocalSachkonto(getAgg('Sachkonto'));
    setLocalInventar(getAgg('InventarAnlagennummer'));
    setSelectedKreditorName(getAgg('KreditorString'));
    setLocalGenehmigungsstatus(getAgg('Genehmigungsstatus'));
    const aggLagerort = getAgg('LagerortCode');
    setLocalLagerortCode(aggLagerort || 'BÖGRA');
    // Bestellstatus wird hierarchisch aggregiert (nicht einfaches getAgg):
    // Geliefert > Teilweise geliefert > Bestellt > Offen
    const bestellstatusWerte = (data.Positionen || []).map(p => p.Bestellstatus).filter(v => v);
    let aggBestellstatus = 'Offen';
    if (bestellstatusWerte.length > 0) {
      if (bestellstatusWerte.every(s => s === 'Geliefert')) {
        aggBestellstatus = 'Geliefert';
      } else if (bestellstatusWerte.some(s => s === 'Geliefert' || s === 'Teilweise geliefert')) {
        aggBestellstatus = 'Teilweise geliefert';
      } else if (bestellstatusWerte.some(s => s === 'Bestellt')) {
        aggBestellstatus = 'Bestellt';
      }
    }
    setLocalBestellstatus(aggBestellstatus);
  };

  const loadPermissions = async (bestellungId?: number, isMounted: boolean = true) => {
    try {
      if (user?.email) {
        const response = await axios.get('/api/permissions/me/resource', {
          params: {
            email: user.email,
            bestellungId: bestellungId
          }
        });
        if (isMounted) {
          setPermissions(response.data);
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
    }
  };

  const loadKreditoren = async (isMounted: boolean = true) => {
    try {
      const data = await kreditorenApi.getAll();
      if (isMounted) {
        setKreditoren(data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Kreditoren:', err);
    }
  };

  const handleKreditorCreated = (newKreditor: IKreditor) => {
    // Füge neuen Kreditor zur Liste hinzu
    setKreditoren((prev) => [...prev, newKreditor]);
  };

  // Filtere Kreditoren basierend auf Toggle und Suchtext
  const getKreditorSuggestions = (): IKreditor[] => {
    // Wenn Toggle "inkl. BC" inaktiv: nur Kreditoren OHNE Kreditorennummer
    const base = includeBCKreditoren
      ? kreditoren
      : kreditoren.filter((k) => !k.Kreditorennummer);

    if (!kreditorSearchText) return base;

    const searchLower = kreditorSearchText.toLowerCase();
    return base.filter(
      (k) =>
        k.Kreditorname?.toLowerCase().includes(searchLower) ||
        k.Kreditorennummer?.toLowerCase().includes(searchLower)
    );
  };

  const loadBadges = async (bestellungId: number) => {
    try {
      const res = await axios.get(`/api/read-status/bestellung/${bestellungId}`);
      setBestellungBadges(res.data);
    } catch {
      // Badge-Fehler ignorieren
    }
  };

  const markAsRead = async (entityType: string, entityId: number) => {
    try {
      await axios.post('/api/read-status/mark', { entityType, entityId });
    } catch {
      // ignorieren
    }
  };

  const loadBestellung = async (bestellungId: number, isMounted: boolean = true) => {
    try {
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      // Wenn es eine archivierte Bestellung ist, rufe die archived API auf
      let data;
      if (isArchived) {
        const response = await axios.get(`/api/bestellungen/archived/${bestellungId}`);
        data = response.data;
      } else {
        data = await bestellungenApi.getById(bestellungId);
      }

      if (isMounted) {
        setBestellung(data);
        syncLocalStatesFromData(data);
        loadBadges(data.Id);
      }
    } catch (err: any) {
      if (isMounted) {
        setError(err.message || 'Fehler beim Laden der Bestellung');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const handleDeleteSelectedPositions = async () => {
    if (!bestellung || selectedPositions.length === 0) return;

    try {
      setLoading(true);
      for (const pos of selectedPositions) {
        if (pos.Id && bestellung.Id) {
          await bestellungenApi.deletePosition(bestellung.Id, pos.Id);
        }
      }
      await loadBestellung(bestellung.Id!);
      setDeleteDialogVisible(false);
      selection.setAllSelected(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Positionen');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllPositions = async () => {
    if (!bestellung || !bestellung.Positionen) return;

    try {
      setLoading(true);
      for (const pos of bestellung.Positionen) {
        if (pos.Id && bestellung.Id) {
          await bestellungenApi.deletePosition(bestellung.Id, pos.Id);
        }
      }
      await loadBestellung(bestellung.Id!);
      setDeleteAllDialogVisible(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen aller Positionen');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPositionForm = (position?: IBestellposition, viewOnly: boolean = false) => {
    setEditingPosition(position);
    setViewOnlyMode(viewOnly);
    setPositionFormOpen(true);
  };

  const handleClosePositionForm = () => {
    setPositionFormOpen(false);
    setEditingPosition(undefined);
    setViewOnlyMode(false);
  };

  const handleSavePosition = async () => {
    if (bestellung?.Id) {
      await loadBestellung(bestellung.Id);
    }
  };

  // Hilfsfunktion: Aggregiere Werte aus allen Positionen
  const getAggregatedValue = (fieldName: keyof IBestellposition): string => {
    if (!bestellung?.Positionen || bestellung.Positionen.length === 0) {
      return '';
    }

    const values = bestellung.Positionen.map((p) => p[fieldName] as string).filter((v) => v);

    if (values.length === 0) {
      return '';
    }

    const firstValue = values[0];
    const allSame = values.every((v) => v === firstValue);

    return allSame ? firstValue : 's. Position';
  };

  // Hilfsfunktion: Setze Wert für alle Positionen (nur API-Calls, kein Reload — lokale States sind führend)
  const handleSetGlobalValue = async (fieldName: keyof IBestellposition, value: string) => {
    if (!bestellung?.Id || !bestellung.Positionen || bestellung.Positionen.length === 0) return;

    try {
      for (const position of bestellung.Positionen) {
        if (position.Id) {
          await bestellungenApi.updatePosition(bestellung.Id, position.Id, {
            [fieldName]: value,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Aktualisieren der Positionen');
    }
  };

  // Archivierung
  const handleArchive = async () => {
    if (!bestellung?.Id) return;

    setArchiving(true);
    try {
      await axios.post(`/api/bestellungen/${bestellung.Id}/archive`);
      setArchiveDialogVisible(false);
      navigate('/bestellungen');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Archivieren der Bestellung');
    } finally {
      setArchiving(false);
    }
  };

  // Dearchivierung
  const handleUnarchive = async () => {
    if (!bestellung?.Id) return;

    setUnarchiving(true);
    try {
      await axios.post(`/api/bestellungen/${bestellung.Id}/unarchive`);
      setUnarchiveDialogVisible(false);
      navigate('/archiv');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Dearchivieren der Bestellung');
    } finally {
      setUnarchiving(false);
    }
  };

  // Prüfe ob Benutzer Owner der Bestellung ist
  const isOwner = !!(bestellung && user && (
    bestellung.Author?.Email?.toLowerCase() === user.email?.toLowerCase() ||
    bestellung.Author?.Title === user.displayName
  ));

  // Prüfe ob Benutzer die Bestellung bearbeiten darf
  // Admin/Einkauf können alles bearbeiten
  // Benutzer mit ErstellenBearbeiten-Rolle können nur ihre eigenen Bestellungen bearbeiten
  // Benutzer mit temporärer Berechtigung können die spezifische Bestellung bearbeiten
  // Archivierte Bestellungen können NICHT bearbeitet werden
  const canEditBestellung = !isArchived && !!(
    permissions?.isAdmin ||
    permissions?.isEinkauf ||
    (permissions?.canEdit && isOwner) ||
    permissions?.hasTemporaryPermission
  );

  const handleApproveAll = async () => {
    if (!bestellung?.Id || !user?.email) return;
    setApprovingAll(true);
    try {
      // Server-seitig berechnete Felder nutzen (emailsMatch-Fallback, kein Strict-Match)
      const flows = await approvalFlowApi.getFlowsForBestellung(bestellung.Id);
      for (const flow of flows) {
        if (flow.CurrentUserCanApprove) {
          await approvalFlowApi.approveStep(flow.Id!, user.email, approveAllComment || undefined, 'Department');
        }
        if (flow.CurrentUserCanApproveGF) {
          await approvalFlowApi.approveStep(flow.Id!, user.email, approveAllComment || undefined, 'Management');
        }
      }
      setApproveAllDialogVisible(false);
      setApproveAllComment('');
      await loadBestellung(bestellung.Id);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Fehler beim Freigeben');
    } finally {
      setApprovingAll(false);
    }
  };

  // BC-Synchronisation: Einkaufsbestellung in BC anlegen
  const handleBcSync = async () => {
    if (!bestellung?.Id) return;
    setBcSyncRunning(true);
    try {
      const response = await axios.post(`/api/bc/${bestellung.Id}`);
      const data = response.data;
      const { zusammenfassung, ergebnisse } = data;
      const successCount = zusammenfassung?.erfolgreich ?? 0;
      const failCount = zusammenfassung?.fehlgeschlagen ?? 0;
      const details = ergebnisse?.map((e: any) =>
        `${e.kreditor}: ${e.erfolg ? `BC-Nr. ${e.documentNo}` : `Fehler – ${e.fehler}`}`
      ).join('\n');

      setBcSyncResultDialog({
        visible: true,
        success: failCount === 0,
        message: failCount === 0
          ? `${successCount} BC-Bestellung(en) erfolgreich angelegt.`
          : `${successCount} erfolgreich, ${failCount} fehlgeschlagen.`,
        details,
      });

      // Bestellung neu laden um BCBestellnummer + Bestellstatus zu zeigen
      await loadBestellung(bestellung.Id);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Unbekannter Fehler';
      setBcSyncResultDialog({ visible: true, success: false, message: `Fehler: ${msg}` });
    } finally {
      setBcSyncRunning(false);
    }
  };

  const handleLieferungMelden = async () => {
    if (!bestellung?.Id) return;
    setLieferungSaving(true);
    try {
      const eintraege = lieferungGesamte
        ? (bestellung.Positionen || []).map((p) => ({ positionId: p.Id!, gelieferteMenge: Number(p.Menge) || 0 }))
        : Object.entries(lieferungPositionen)
            .filter(([, v]) => v.checked)
            .map(([id, v]) => ({ positionId: Number(id), gelieferteMenge: parseFloat(v.menge) || 0 }));

      if (eintraege.length === 0) {
        setLieferungSaving(false);
        return;
      }
      await bestellungenApi.meldelieferung(bestellung.Id, eintraege, lieferungGesamte);
      setLieferungDialogVisible(false);
      await loadBestellung(bestellung.Id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Melden der Lieferung');
    } finally {
      setLieferungSaving(false);
    }
  };

  // CommandBar Items basierend auf Berechtigungen
  const commandBarItems: ICommandBarItemProps[] = React.useMemo(() => {
    const items: ICommandBarItemProps[] = [];

    // Archivierte Bestellungen: Nur Dearchivieren-Button
    if (isArchived) {
      if (permissions?.canArchive) {
        items.push({
          key: 'unarchive',
          text: 'Dearchivieren',
          iconProps: { iconName: 'Undo' },
          onClick: () => setUnarchiveDialogVisible(true),
        });
      }
      return items;
    }

    // Hilfsfunktion: tooltipHostProps wenn Hilfe-Modus aktiv
    const tip = helpMode ? (helpText: string) => ({ tooltipHostProps: { content: helpText } }) : () => ({});

    // In BC anlegen (nur Admin und Einkauf, nur wenn alle Positionen Freigegeben)
    if (permissions?.isAdmin || permissions?.isEinkauf) {
      const positionen = bestellung?.Positionen || [];
      const alleFreigegeben = positionen.length > 0 &&
        positionen.every(p => p.Genehmigungsstatus === 'Freigegeben');
      items.push({
        key: 'bcAnlegen',
        text: 'In BC anlegen',
        iconProps: { iconName: 'Sync' },
        disabled: !alleFreigegeben || bcSyncRunning,
        onClick: handleBcSync,
        ...tip('Legt für alle freigegebenen Positionen eine BC-Einkaufsbestellung an. Nur wenn alle Positionen vollständig freigegeben sind.'),
      });
    }

    // Genehmigungsflow anzeigen (für alle mit canApprove oder Owner)
    if (permissions?.canApprove || isOwner) {
      items.push({
        key: 'genehmigungsflow',
        text: 'Genehmigungsflow',
        iconProps: { iconName: 'DocumentApproval' },
        onClick: () => setApprovalFlowPanelOpen(true),
        ...tip('Öffnet das Freigabe-Panel: Genehmigungsflows anlegen, einsehen, freigeben oder ablehnen.'),
      });
    }

    // Alle Positionen freigeben (für canApprove)
    if (permissions?.canApprove) {
      items.push({
        key: 'alleFreigeben',
        text: 'Alle freigeben',
        iconProps: { iconName: 'CheckMark' },
        onClick: () => setApproveAllDialogVisible(true),
        ...tip('Genehmigt alle offenen Freigabeschritte dieser Bestellung, für die Sie zuständig sind – auf einmal.'),
      });
    }

    // Nur mit Edit-Rechten: Neue Position, Löschen
    if (permissions?.canEdit && (permissions.isAdmin || permissions.isEinkauf || isOwner)) {
      items.push({
        key: 'neuePosition',
        text: 'Neue Position',
        iconProps: { iconName: 'Add' },
        onClick: () => handleOpenPositionForm(),
        ...tip('Fügt eine neue Bestellposition hinzu: Artikel, Kreditor, Menge, Preis, Kostenstelle usw.'),
      });

      items.push({
        key: 'positionenLoeschen',
        text: 'Ausgewählte löschen',
        iconProps: { iconName: 'Delete' },
        disabled: selectedPositions.length === 0,
        onClick: () => setDeleteDialogVisible(true),
        ...tip('Löscht die in der Tabelle ausgewählten Positionen (Checkboxen links).'),
      });

      items.push({
        key: 'alleLoeschen',
        text: 'Alle löschen',
        iconProps: { iconName: 'DeleteRows' },
        disabled: !bestellung?.Positionen || bestellung.Positionen.length === 0,
        onClick: () => setDeleteAllDialogVisible(true),
        ...tip('Löscht alle Positionen dieser Bestellung.'),
      });
    }

    // Archivieren (nur Admin und Einkauf)
    if (permissions?.canArchive) {
      items.push({
        key: 'archivieren',
        text: 'Archivieren',
        iconProps: { iconName: 'Archive' },
        onClick: () => setArchiveDialogVisible(true),
        ...tip('Verschiebt die Bestellung ins Archiv. Nur Admin und Einkauf können archivieren.'),
      });
    }

    // Lieferung melden (für alle eingeloggten Benutzer)
    if (bestellung?.Positionen && bestellung.Positionen.length > 0) {
      items.push({
        key: 'lieferungMelden',
        text: 'Lieferung melden',
        iconProps: { iconName: 'DeliveryTruck' },
        onClick: () => {
          const initState: Record<number, { checked: boolean; menge: string }> = {};
          (bestellung.Positionen || []).forEach((p) => {
            initState[p.Id!] = { checked: false, menge: String(p.Menge || '') };
          });
          setLieferungPositionen(initState);
          setLieferungGesamte(false);
          setLieferungDialogVisible(true);
        },
        ...tip('Meldet die Lieferung von Positionen. Für alle Benutzer verfügbar.'),
      });
    }

    return items;
  }, [permissions, bestellung, selectedPositions, isOwner, isArchived, helpMode, bcSyncRunning]);

  // Spalten basierend auf Berechtigungen
  const columns: IColumn[] = React.useMemo(() => {
    const cols: IColumn[] = [
      {
        key: 'posID',
        name: 'Best.-ID / Pos',
        fieldName: 'PosID',
        minWidth: 90,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
              {item.BestellungId}
            </Text>
            <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>/</Text>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
              {item.PosID}
            </Text>
          </Stack>
        ),
      },
      {
        key: 'bcbestellnummer_col1',
        name: 'BC-Bestellnr.',
        fieldName: 'BCBestellnummer',
        minWidth: 90,
        maxWidth: 130,
        isResizable: true,
        onRender: (item: IBestellposition) => {
          if (!item.BCBestellnummer) return <Text variant="small" styles={{ root: { color: '#a19f9d' } }}>–</Text>;
          const ebNr = encodeURIComponent(item.BCBestellnummer);
          const bcBrowserUrl = `https://boegra.faveo365.com/W6741025118555748570/?tenant=t5257376201000231284&company=B%C3%96GRATechnologie%20GmbH&page=50&filter=No.%20IS%20%27${ebNr}%27`;
          const bcClientUrl = `dynamicsnav://boegra.faveo365.com:9246/NSTBOEGRA/B%C3%96GRATechnologie%20GmbH/runpage?tenant=T5257376201000231284&page=50&personalization=50&$filter=%27Purchase%20Header%27.%27No.%27%20IS%20%27%40%2A${ebNr}%2A%27&mode=Edit`;
          return (
            <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
              <Stack>
                <Text variant="small">{item.BCBestellnummer}</Text>
                {item.OffeneMenge !== undefined && item.OffeneMenge !== null && (
                  <Text variant="tiny" styles={{ root: { color: '#605e5c' } }}>
                    offen: {item.OffeneMenge}
                  </Text>
                )}
              </Stack>
              <TooltipHost content="In BC öffnen">
                <IconButton
                  iconProps={{ iconName: 'OpenInNewWindow' }}
                  styles={{ root: { height: 20, width: 20 }, icon: { fontSize: 12 } }}
                  onClick={() => setBcOpenDialog({ visible: true, browserUrl: bcBrowserUrl, clientUrl: bcClientUrl })}
                />
              </TooltipHost>
            </Stack>
          );
        },
      },
      {
        key: 'artikelname',
        name: 'Artikel',
        fieldName: 'Artikelname',
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Stack>
            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
              {item.Artikelname || '-'}
            </Text>
            {item.Artikelnummer && (
              <Text variant="tiny" styles={{ root: { color: '#605e5c' } }}>
                Art.Nr: {item.Artikelnummer}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'kreditor',
        name: 'Kreditor',
        fieldName: 'KreditorString',
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Text variant="small">{item.KreditorString || '-'}</Text>
        ),
      },
      {
        key: 'menge',
        name: 'Menge',
        fieldName: 'Menge',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Text variant="small">
            {item.Menge} {item.Mengeneinheit || 'Stk'}
          </Text>
        ),
      },
      {
        key: 'einzelpreis',
        name: 'Einzelpreis',
        fieldName: 'Einzelpreis',
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Text variant="small">
            {new Intl.NumberFormat('de-DE', {
              style: 'currency',
              currency: 'EUR',
            }).format(item.Einzelpreis || 0)}
          </Text>
        ),
      },
      {
        key: 'gesamtpreis',
        name: 'Gesamt',
        fieldName: 'PreisGesamt',
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
            {new Intl.NumberFormat('de-DE', {
              style: 'currency',
              currency: 'EUR',
            }).format(item.PreisGesamt || 0)}
          </Text>
        ),
      },
      {
        key: 'genehmigungsstatus',
        name: 'Genehmigung',
        fieldName: 'Genehmigungsstatus',
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <TooltipHost content={item.Genehmigungsstatus || 'Offen'}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: getGenehmigungsstatusColor(item.Genehmigungsstatus || 'Offen'),
                  border: '1px solid #605e5c',
                }}
              />
            </TooltipHost>
            <Text variant="small">{item.Genehmigungsstatus || 'Offen'}</Text>
          </Stack>
        ),
      },
      {
        key: 'bestellstatus',
        name: 'Bestellstatus',
        fieldName: 'Bestellstatus',
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: IBestellposition) => (
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <TooltipHost content={item.Bestellstatus || 'Eingereicht'}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: getBestellstatusColor(item.Bestellstatus || 'Eingereicht'),
                  border: '1px solid #605e5c',
                }}
              />
            </TooltipHost>
            <Text variant="small">{item.Bestellstatus || 'Eingereicht'}</Text>
          </Stack>
        ),
      },
    ];

    // Aktionen-Spalte - immer anzeigen (Lupe für alle, Stift nur mit Edit-Rechten)
    cols.push({
      key: 'actions',
      name: 'Aktionen',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: IBestellposition) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          {/* Lupe für alle Benutzer (View-Only) */}
          <IconButton
            iconProps={{ iconName: 'View' }}
            title="Position ansehen"
            onClick={() => handleOpenPositionForm(item, true)}
          />

          {/* Stift nur für Benutzer mit Edit-Rechten */}
          {canEditBestellung && (
            <IconButton
              iconProps={{ iconName: 'Edit' }}
              title="Position bearbeiten"
              onClick={() => handleOpenPositionForm(item, false)}
            />
          )}

          {/* Link-Icon wenn vorhanden */}
          {item.Artikelhyperlink && (
            <IconButton
              iconProps={{ iconName: 'Link' }}
              title="Artikel-Link öffnen"
              onClick={() => window.open(item.Artikelhyperlink, '_blank')}
            />
          )}
        </Stack>
      ),
    });

    return cols;
  }, [permissions, isOwner, canEditBestellung]);

  if (loading && !bestellung) {
    return (
      <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
        <Spinner label="Lade Bestellung..." />
      </Stack>
    );
  }

  if (error && !bestellung) {
    return (
      <Stack tokens={{ childrenGap: 20, padding: 20 }}>
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
        <DefaultButton text="Zurück zur Übersicht" onClick={() => navigate('/bestellungen')} />
      </Stack>
    );
  }

  if (!bestellung) {
    return (
      <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
        <Text variant="large">Bestellung nicht gefunden</Text>
        <DefaultButton text="Zurück zur Übersicht" onClick={() => navigate('/bestellungen')} />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
          <IconButton
            iconProps={{ iconName: 'Back' }}
            title="Zurück zur Übersicht"
            onClick={() => navigate('/bestellungen')}
          />
          <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
            {bestellung.Title || 'Bestellung'}
          </Text>
        </Stack>
      </Stack>

      {/* Error Message */}
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* Bestellung Info Card */}
      <Stack
        styles={{
          root: {
            padding: '20px',
            backgroundColor: 'white',
            boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)',
            borderRadius: '2px',
          },
        }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          {/* Zeile 1 */}
          <Stack horizontal tokens={{ childrenGap: 32 }} wrap>
{(() => {
              const nummern = [...new Set((bestellung.Positionen || []).filter(p => p.BCBestellnummer).map(p => p.BCBestellnummer!))];
              if (nummern.length === 0) return null;
              const ebNummer = nummern.length === 1 ? nummern[0] : 's. Position';
              return (
                <Stack>
                  <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                    EB-Nummer (BC)
                  </Text>
                  <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, color: '#0078d4' } }}>
                    {ebNummer}
                  </Text>
                </Stack>
              );
            })()}
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Anfordernde Person
              </Text>
              <Text variant="mediumPlus">{bestellung.AnforderndePersonen || 'Unbekannt'}</Text>
            </Stack>
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Erstellt von
              </Text>
              <Text variant="mediumPlus">
                {bestellung.Author?.Title || bestellung.AnforderndePersonen || 'Unbekannt'}
              </Text>
            </Stack>
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Erstellungsdatum
              </Text>
              <Text variant="mediumPlus">
                {bestellung.Erstellungsdatum
                  ? new Date(bestellung.Erstellungsdatum).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })
                  : '-'}
              </Text>
            </Stack>
          </Stack>

          {/* Zeile 2 */}
          <Stack horizontal tokens={{ childrenGap: 32 }} wrap>
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Genehmigt:
              </Text>
              <TooltipHost content={bestellung.Genehmigungsstatus}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: getGenehmigungsstatusColor(bestellung.Genehmigungsstatus),
                    border: '1px solid #605e5c',
                  }}
                />
              </TooltipHost>
              <Text variant="mediumPlus">{bestellung.Genehmigungsstatus}</Text>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Status:
              </Text>
              <TooltipHost content={bestellung.Bestellstatus}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: getBestellstatusColor(bestellung.Bestellstatus),
                    border: '1px solid #605e5c',
                  }}
                />
              </TooltipHost>
              <Text variant="mediumPlus">{bestellung.Bestellstatus}</Text>
            </Stack>

            {/* Vertraulich Toggle - nur für Owner, Admin, Einkauf sichtbar */}
            {(isOwner || permissions?.isAdmin || permissions?.isEinkauf) && (
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                <Toggle
                  label="Vertraulich"
                  inlineLabel
                  checked={!!bestellung.Vertraulich}
                  disabled={isArchived}
                  onChange={async (_, checked) => {
                    if (bestellung.Id) {
                      try {
                        await axios.put(`/api/bestellungen/${bestellung.Id}`, { ...bestellung, Vertraulich: !!checked });
                        setBestellung({ ...bestellung, Vertraulich: !!checked });
                      } catch (err) {
                        console.error('Fehler beim Setzen von Vertraulich:', err);
                        setError('Fehler beim Ändern der Vertraulichkeit');
                      }
                    }
                  }}
                  styles={{ root: { marginBottom: 0 } }}
                />
                {bestellung.Vertraulich && (
                  <TooltipHost content="Nur Admin, Einkauf, GF, Freigeber und Ersteller sehen diese Bestellung">
                    <Icon iconName="Lock" styles={{ root: { color: '#d13438', fontSize: 16 } }} />
                  </TooltipHost>
                )}
              </Stack>
            )}
            {/* Vertraulich-Anzeige für andere Berechtigte (nur lesen) */}
            {!(isOwner || permissions?.isAdmin || permissions?.isEinkauf) && bestellung.Vertraulich && (
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                <TooltipHost content="Vertrauliche Bestellung">
                  <Icon iconName="Lock" styles={{ root: { color: '#d13438', fontSize: 16 } }} />
                </TooltipHost>
                <Text variant="small" styles={{ root: { color: '#d13438' } }}>Vertraulich</Text>
              </Stack>
            )}
          </Stack>

          {/* Zeile 3 */}
          <Stack horizontal tokens={{ childrenGap: 32 }} wrap>
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Kostenstelle
              </Text>
              <Text variant="mediumPlus">{bestellung.AnforderndeKostenstelle || '-'}</Text>
            </Stack>
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Liefertermin
              </Text>
              <Text variant="mediumPlus">
                {bestellung.Liefertermin && bestellung.Liefertermin !== 's. Position'
                  ? `KW ${bestellung.Liefertermin}`
                  : bestellung.Liefertermin || '-'}
              </Text>
            </Stack>
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Gesamtsumme
              </Text>
              <Text variant="xLarge" styles={{ root: { fontWeight: 600, color: '#0078d4' } }}>
                {new Intl.NumberFormat('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(bestellung.GesamtSumme || 0)}
              </Text>
            </Stack>
          </Stack>

          {bestellung.HinweistextZurBestellung && (
            <Stack>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Hinweistext
              </Text>
              <Text variant="medium">{bestellung.HinweistextZurBestellung}</Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Tabs */}
      <Pivot
        styles={{ root: { backgroundColor: 'white', boxShadow: '0 1.6px 3.6px 0 rgba(0,0,0,.132), 0 0.3px 0.9px 0 rgba(0,0,0,.108)', borderRadius: '2px' } }}
        onLinkClick={(item) => {
          if (!bestellung?.Id) return;
          const key = item?.props.itemKey;
          if (key === 'kommentare') {
            markAsRead('BestellungKommentare', bestellung.Id).then(() => loadBadges(bestellung.Id!));
          } else if (key === 'dokumente') {
            markAsRead('BestellungDokumente', bestellung.Id).then(() => loadBadges(bestellung.Id!));
          }
        }}
      >

        {/* ── Tab 1: Positionen ── */}
        <PivotItem headerText="Positionen" itemIcon="BulletedList">
          <Stack tokens={{ padding: '0 0 16px 0' }}>

      {/* Globale Einstellungen */}
      <Stack styles={{ root: { padding: '20px' } }}>
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          onClick={() => setGlobalSettingsExpanded(!globalSettingsExpanded)}
          styles={{
            root: {
              cursor: 'pointer',
              selectors: { ':hover': { backgroundColor: '#f3f2f1' } },
              padding: '8px',
              marginLeft: '-8px',
              marginRight: '-8px',
              borderRadius: '2px',
            },
          }}
        >
          <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
            Optionen für alle Positionen
          </Text>
          <Icon iconName={globalSettingsExpanded ? 'ChevronUp' : 'ChevronDown'} />
        </Stack>

        {globalSettingsExpanded && (
          <>
            <Text
              variant="small"
              styles={{ root: { color: '#605e5c', marginTop: '8px', marginBottom: '16px' } }}
            >
              Wenn nicht "s. Position", gilt das für alle Positionen gleichzeitig
            </Text>

            <Stack tokens={{ childrenGap: 16 }}>
              {/* Zeile 0: EB-Nummer (BC) */}
              {(() => {
                const isMixed = localBCBestellnummer === 's. Position';
                const canEditBCNr = !isArchived && (permissions?.isAdmin || permissions?.isEinkauf);
                return (
                  <TextField
                    label="EB-Nummer (BC)"
                    placeholder={isMixed ? 's. Position' : 'z.B. EB000123'}
                    value={isMixed ? '' : localBCBestellnummer}
                    onChange={(_, value) => {
                      if (!isMixed) setLocalBCBestellnummer(value || '');
                    }}
                    onBlur={() => {
                      if (!isMixed && canEditBCNr) {
                        handleSetGlobalValue('BCBestellnummer', localBCBestellnummer);
                      }
                    }}
                    disabled={!canEditBCNr || isMixed}
                    description={isMixed ? 'Positionen haben unterschiedliche EB-Nummern – bitte direkt an der Position ändern' : undefined}
                    styles={{ root: { maxWidth: 300 } }}
                  />
                );
              })()}

              {/* Zeile 1: Liefertermin + Lagerortcode */}
              <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                <TextField
                  label="Liefertermin (KW)"
                  placeholder="z.B. 15 oder 15-2026"
                  value={localLieferterminKW}
                  onChange={(_, value) => setLocalLieferterminKW(value || '')}
                  onBlur={() => {
                    if (localLieferterminKW && localLieferterminKW !== 's. Position') {
                      handleSetGlobalValue('LieferterminKW', localLieferterminKW);
                    }
                  }}
                  disabled={!canEditBestellung}
                  styles={{ root: { flex: 1, minWidth: 200 } }}
                />
                <Dropdown
                  label="Lagerort-Code"
                  selectedKey={localLagerortCode === 's. Position' ? undefined : (localLagerortCode || 'BÖGRA')}
                  placeholder={localLagerortCode === 's. Position' ? 's. Position' : undefined}
                  options={(() => {
                    const opts = lagerorte.map(l => ({
                      key: l.Code,
                      text: l.Name ? `${l.Code} – ${l.Name}` : l.Code,
                    }));
                    if (!opts.find(o => o.key === 'BÖGRA')) {
                      opts.unshift({ key: 'BÖGRA', text: 'BÖGRA – Bögra Hauptlager' });
                    }
                    return opts;
                  })()}
                  onChange={(_, option) => {
                    if (option?.key) {
                      setLocalLagerortCode(option.key as string);
                      handleSetGlobalValue('LagerortCode', option.key as string);
                    }
                  }}
                  disabled={!canEditBestellung}
                  styles={{ root: { flex: 1, minWidth: 200 } }}
                />
              </Stack>

              {/* Zeile 2: Genehmigt + Status */}
              <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                <Dropdown
                  label="Genehmigt:"
                  selectedKey={localGenehmigungsstatus}
                  onChange={(_, option) => {
                    if (option?.key && option.key !== 's. Position') {
                      setLocalGenehmigungsstatus(option.key as string);
                      handleSetGlobalValue('Genehmigungsstatus', option.key as string);
                    }
                  }}
                  disabled={!canEditBestellung}
                  options={[
                    { key: 'Offen', text: 'Offen' },
                    { key: 'Freigegeben', text: 'Freigegeben' },
                    { key: 'Teilweise freigegeben', text: 'Teilweise freigegeben' },
                    { key: 'Abgelehnt', text: 'Abgelehnt' },
                    { key: 's. Position', text: 's. Position' },
                  ]}
                  onRenderTitle={(options) => {
                    if (!options || options.length === 0) return null;
                    const option = options[0];
                    const bgColor = getGenehmigungsstatusColor(option.key as string);
                    return (
                      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: bgColor,
                            border: '1px solid #605e5c',
                          }}
                        />
                        <Text>{option.text}</Text>
                      </Stack>
                    );
                  }}
                  onRenderOption={(option) => {
                    if (!option) return null;
                    const bgColor = getGenehmigungsstatusColor(option.key as string);
                    return (
                      <Stack
                        horizontal
                        tokens={{ childrenGap: 8 }}
                        verticalAlign="center"
                        styles={{
                          root: {
                            padding: '4px 8px',
                            backgroundColor: option.key === 's. Position' ? 'transparent' : bgColor,
                            color: option.key === 's. Position' ? '#323130' : 'white',
                          },
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: bgColor,
                            border: '1px solid white',
                          }}
                        />
                        <Text>{option.text}</Text>
                      </Stack>
                    );
                  }}
                  styles={{ root: { flex: 1, minWidth: 200 } }}
                />
                <Dropdown
                  label="Status:"
                  selectedKey={localBestellstatus}
                  onChange={(_, option) => {
                    if (option?.key && option.key !== 's. Position') {
                      setLocalBestellstatus(option.key as string);
                      handleSetGlobalValue('Bestellstatus', option.key as string);
                    }
                  }}
                  disabled={!canEditBestellung}
                  options={[
                    { key: 'Eingereicht', text: 'Eingereicht' },
                    { key: 'Bestellt', text: 'Bestellt' },
                    { key: 'Teilweise geliefert', text: 'Teilweise geliefert' },
                    { key: 'Geliefert', text: 'Geliefert' },
                    { key: 's. Position', text: 's. Position' },
                  ]}
                  onRenderTitle={(options) => {
                    if (!options || options.length === 0) return null;
                    const option = options[0];
                    const bgColor = getBestellstatusColor(option.key as string);
                    return (
                      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: bgColor,
                            border: '1px solid #605e5c',
                          }}
                        />
                        <Text>{option.text}</Text>
                      </Stack>
                    );
                  }}
                  onRenderOption={(option) => {
                    if (!option) return null;
                    const bgColor = getBestellstatusColor(option.key as string);
                    return (
                      <Stack
                        horizontal
                        tokens={{ childrenGap: 8 }}
                        verticalAlign="center"
                        styles={{
                          root: {
                            padding: '4px 8px',
                            backgroundColor: option.key === 's. Position' ? 'transparent' : bgColor,
                            color: option.key === 's. Position' ? '#323130' : 'white',
                          },
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: bgColor,
                            border: '1px solid white',
                          }}
                        />
                        <Text>{option.text}</Text>
                      </Stack>
                    );
                  }}
                  styles={{ root: { flex: 1, minWidth: 200 } }}
                />
              </Stack>

              {/* Zeile 3: Kreditor mit Toggle und Suchfeld */}
              <Stack tokens={{ childrenGap: 6 }}>
                <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                  <Label>Kreditor</Label>
                  <Toggle
                    label="inkl. BC"
                    inlineLabel
                    checked={includeBCKreditoren}
                    onChange={(_, checked) => setIncludeBCKreditoren(!!checked)}
                    disabled={!canEditBestellung}
                    styles={{ root: { marginBottom: 0 } }}
                  />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                  <div ref={kreditorContainerRef} style={{ flexGrow: 1, position: 'relative' }}>
                    <TextField
                      placeholder="Kreditor suchen (Name oder Nummer)..."
                      value={kreditorSearchText || selectedKreditorName}
                      onFocus={() => {
                        if (kreditorSearchText) setShowKreditorDropdown(true);
                      }}
                      onChange={(_, value) => {
                        setKreditorSearchText(value || '');
                        setShowKreditorDropdown(!!(value && value.length > 0));
                      }}
                      disabled={!canEditBestellung}
                      styles={{ root: { width: '100%' } }}
                    />
                    {showKreditorDropdown && kreditorSearchText && (
                      <Callout
                        target={kreditorContainerRef}
                        isBeakVisible={false}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        onDismiss={() => setShowKreditorDropdown(false)}
                        calloutMaxWidth={kreditorContainerRef.current?.offsetWidth || 400}
                        styles={{ calloutMain: { minWidth: kreditorContainerRef.current?.offsetWidth || 300 } }}
                      >
                        <Stack>
                          {getKreditorSuggestions().length === 0 ? (
                            <Stack tokens={{ padding: '8px 12px' }}>
                              <Text styles={{ root: { color: '#605e5c' } }}>Keine Kreditoren gefunden</Text>
                            </Stack>
                          ) : (
                            getKreditorSuggestions().map((k) => (
                              <Stack
                                key={k.Id}
                                horizontal
                                tokens={{ padding: '7px 12px', childrenGap: 8 }}
                                styles={{
                                  root: {
                                    cursor: 'pointer',
                                    selectors: { ':hover': { backgroundColor: '#f3f2f1' } },
                                  },
                                }}
                                onClick={() => {
                                  setSelectedKreditorName(k.Kreditorname || '');
                                  setKreditorSearchText('');
                                  setShowKreditorDropdown(false);
                                  handleSetGlobalValue('KreditorString', k.Kreditorname || '');
                                }}
                              >
                                <Text>{k.Kreditorname}</Text>
                                {k.Kreditorennummer && (
                                  <Text styles={{ root: { color: '#605e5c' } }}>
                                    ({k.Kreditorennummer})
                                  </Text>
                                )}
                              </Stack>
                            ))
                          )}
                        </Stack>
                      </Callout>
                    )}
                  </div>
                  <PrimaryButton
                    text="Neu..."
                    iconProps={{ iconName: 'Add' }}
                    onClick={() => setShowKreditorDialog(true)}
                    disabled={!canEditBestellung}
                    styles={{ root: { marginBottom: '4px' } }}
                  />
                </Stack>
              </Stack>

              {/* Zeile 3: Kostenstelle, Sachkonto, Inventar */}
              <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                <TextField
                  label="Für Kostenstelle"
                  placeholder="z.B. 9430"
                  value={localKostenstelle}
                  onChange={(_, value) => setLocalKostenstelle(value || '')}
                  onBlur={() => {
                    if (localKostenstelle && localKostenstelle !== 's. Position') {
                      handleSetGlobalValue('EmpfangendeKostenstelle', localKostenstelle);
                    }
                  }}
                  disabled={!canEditBestellung}
                  styles={{ root: { width: '33%', minWidth: 200 } }}
                />
                <TextField
                  label="Sachkonto"
                  value={localSachkonto}
                  onChange={(_, value) => setLocalSachkonto(value || '')}
                  onBlur={() => {
                    if (localSachkonto && localSachkonto !== 's. Position') {
                      handleSetGlobalValue('Sachkonto', localSachkonto);
                    }
                  }}
                  disabled={!canEditBestellung}
                  styles={{ root: { width: '33%', minWidth: 200 } }}
                />
                <TextField
                  label="Inventar-/Anlagennummer"
                  value={localInventar}
                  onChange={(_, value) => setLocalInventar(value || '')}
                  onBlur={() => {
                    if (localInventar && localInventar !== 's. Position') {
                      handleSetGlobalValue('InventarAnlagennummer', localInventar);
                    }
                  }}
                  disabled={!canEditBestellung}
                  styles={{ root: { width: '33%', minWidth: 200 } }}
                />
              </Stack>
            </Stack>
          </>
        )}
      </Stack>

      {/* CommandBar */}
      <CommandBar items={commandBarItems} />

      {/* Positionen Table */}
      <Stack>
        <Stack styles={{ root: { padding: '16px 16px 8px 16px' } }}>
          <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
            Bestellpositionen ({bestellung.AnzahlPositionen || 0})
          </Text>
        </Stack>

        {bestellung.Positionen && bestellung.Positionen.length > 0 ? (
          <DetailsList
            items={bestellung.Positionen}
            columns={columns}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selection={selection}
            selectionMode={SelectionMode.multiple}
            selectionPreservedOnEmptyClick={true}
            checkButtonAriaLabel="Zeile auswählen"
          />
        ) : (
          <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
            <Icon iconName="BoxMultiplySolid" styles={{ root: { fontSize: 48, color: '#c8c6c4' } }} />
            <Text variant="large" styles={{ root: { marginTop: 16, color: '#605e5c' } }}>
              Keine Bestellpositionen vorhanden
            </Text>
            <PrimaryButton
              text="Erste Position hinzufügen"
              iconProps={{ iconName: 'Add' }}
              onClick={() => handleOpenPositionForm()}
              styles={{ root: { marginTop: 16 } }}
            />
          </Stack>
        )}
      </Stack>

          </Stack>
        </PivotItem>

        {/* ── Tab 2: Kommentare ── */}
        <PivotItem
          itemKey="kommentare"
          itemIcon="Comment"
          onRenderItemLink={() => (
            <TabBadge
              label="Kommentare"
              total={bestellungBadges?.kommentare.total ?? 0}
              unread={bestellungBadges?.kommentare.unread ?? 0}
              hasMention={bestellungBadges?.kommentare.hasMention ?? false}
            />
          )}
        >
          <Stack tokens={{ padding: '16px' }}>
            {bestellung && bestellung.Positionen && (
              <KommentarSektion
                bestellungId={bestellung.Id!}
                positionen={bestellung.Positionen}
                onKommentarChange={async () => {
                  // Erst markieren (LastReadAt = jetzt >= Created des neuen Kommentars),
                  // dann laden – sonst zeigt loadBadges den eigenen Kommentar als ungelesen
                  if (bestellung.Id) await markAsRead('BestellungKommentare', bestellung.Id);
                  loadBestellung(bestellung.Id!);
                }}
              />
            )}
          </Stack>
        </PivotItem>

        {/* ── Tab 3: Dokumente ── */}
        <PivotItem
          itemKey="dokumente"
          itemIcon="Attach"
          onRenderItemLink={() => (
            <TabBadge
              label="Dokumente"
              total={bestellungBadges?.dokumente.total ?? 0}
              unread={bestellungBadges?.dokumente.unread ?? 0}
            />
          )}
        >
          <Stack tokens={{ padding: '16px' }}>
            {bestellung && (
              <DokumentePanel
                bestellungId={bestellung.Id!}
                positionen={bestellung.Positionen?.map(p => ({
                  Id: p.Id!,
                  PosID: p.PosID ?? p.Id!,
                  Artikelname: p.Artikelname || '',
                }))}
                canUpload={!!(permissions?.canCreate || permissions?.isAdmin || permissions?.isEinkauf)}
                canDeleteAll={!!(permissions?.isAdmin || permissions?.isEinkauf)}
                currentUserEmail={user?.email || ''}
                onDokumentChange={async () => {
                  if (bestellung.Id) {
                    await markAsRead('BestellungDokumente', bestellung.Id);
                    loadBadges(bestellung.Id);
                  }
                }}
              />
            )}
          </Stack>
        </PivotItem>

        {/* ── Tab 4: Statusverlauf ── */}
        <PivotItem headerText="Statusverlauf" itemIcon="History">
          <Stack tokens={{ padding: '16px' }}>
            {bestellung && (
              <StatusverlaufPanel bestellungId={bestellung.Id!} />
            )}
          </Stack>
        </PivotItem>

      </Pivot>

      {/* Delete Dialogs */}
      <Dialog
        hidden={!deleteDialogVisible}
        onDismiss={() => setDeleteDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Positionen löschen',
          subText: `Möchten Sie die ${selectedPositions.length} ausgewählte(n) Position(en) wirklich löschen?`,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleDeleteSelectedPositions} text="Löschen" />
          <DefaultButton onClick={() => setDeleteDialogVisible(false)} text="Abbrechen" />
        </DialogFooter>
      </Dialog>

      <Dialog
        hidden={!deleteAllDialogVisible}
        onDismiss={() => setDeleteAllDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Alle Positionen löschen',
          subText: 'Möchten Sie wirklich ALLE Positionen dieser Bestellung löschen?',
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleDeleteAllPositions} text="Alle löschen" />
          <DefaultButton onClick={() => setDeleteAllDialogVisible(false)} text="Abbrechen" />
        </DialogFooter>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog
        hidden={!archiveDialogVisible}
        onDismiss={() => setArchiveDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellung archivieren',
          subText: 'Möchten Sie diese Bestellung wirklich archivieren? Die Bestellung und alle zugehörigen Positionen werden ins Archiv verschoben.',
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleArchive} text="Archivieren" disabled={archiving} />
          <DefaultButton onClick={() => setArchiveDialogVisible(false)} text="Abbrechen" disabled={archiving} />
        </DialogFooter>
      </Dialog>

      {/* Unarchive Dialog */}
      <Dialog
        hidden={!unarchiveDialogVisible}
        onDismiss={() => setUnarchiveDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Bestellung dearchivieren',
          subText: 'Möchten Sie diese Bestellung wirklich dearchivieren? Die Bestellung wird zurück in die aktive Liste verschoben.',
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleUnarchive} text="Dearchivieren" disabled={unarchiving} />
          <DefaultButton onClick={() => setUnarchiveDialogVisible(false)} text="Abbrechen" disabled={unarchiving} />
        </DialogFooter>
      </Dialog>

      {/* Position Formular Panel */}
      {bestellung && (
        <BestellpositionFormular
          isOpen={positionFormOpen}
          onDismiss={handleClosePositionForm}
          onSave={handleSavePosition}
          bestellungId={bestellung.Id!}
          position={editingPosition}
          permissions={permissions}
          isOwner={isOwner}
          viewOnly={viewOnlyMode}
          defaultValues={{
            KreditorString: selectedKreditorName,
            EmpfangendeKostenstelle: localKostenstelle,
            Sachkonto: localSachkonto,
            InventarAnlagennummer: localInventar,
            LieferterminKW: localLieferterminKW,
            LagerortCode: localLagerortCode,
            Genehmigungsstatus: localGenehmigungsstatus as any,
            Bestellstatus: localBestellstatus as any,
          }}
        />
      )}

      {/* Alle Positionen freigeben Dialog */}
      <Dialog
        hidden={!approveAllDialogVisible}
        onDismiss={() => setApproveAllDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Alle Positionen freigeben',
          subText: 'Genehmigt alle offenen Flows dieser Bestellung, in denen Sie als Genehmiger eingetragen sind.',
        }}
      >
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: '0 24px 12px' } }}>
          <TextField
            label="Kommentar (optional)"
            placeholder="Ihr Kommentar..."
            value={approveAllComment}
            onChange={(_, v) => setApproveAllComment(v || '')}
            multiline
            rows={3}
          />
        </Stack>
        <DialogFooter>
          <PrimaryButton onClick={handleApproveAll} text="Alle freigeben" disabled={approvingAll} />
          <DefaultButton onClick={() => setApproveAllDialogVisible(false)} text="Abbrechen" disabled={approvingAll} />
        </DialogFooter>
      </Dialog>

      {/* Approval Flow Panel */}
      {bestellung && (
        <ApprovalFlowPanel
          isOpen={approvalFlowPanelOpen}
          onDismiss={() => setApprovalFlowPanelOpen(false)}
          onApprovalChange={() => loadBestellung(bestellung.Id!)}
          bestellungId={bestellung.Id!}
        />
      )}

      {/* Dialog zum Anlegen neuer Kreditoren */}
      <KreditorAnlegenDialog
        isOpen={showKreditorDialog}
        onDismiss={() => setShowKreditorDialog(false)}
        onSuccess={handleKreditorCreated}
      />

      {/* BC-Sync laufend */}
      {bcSyncRunning && (
        <Dialog
          hidden={false}
          dialogContentProps={{ type: DialogType.normal, title: 'BC-Bestellung wird angelegt…' }}
          modalProps={{ isBlocking: true }}
        >
          <Stack tokens={{ childrenGap: 12 }} horizontalAlign="center" styles={{ root: { padding: '16px 0' } }}>
            <Spinner label="Puppeteer-Session läuft, BC wird befüllt…" />
            <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
              Dieser Vorgang kann 30–90 Sekunden dauern.
            </Text>
          </Stack>
        </Dialog>
      )}

      {/* BC-Deeplink: Browser oder Client-App */}
      <Dialog
        hidden={!bcOpenDialog.visible}
        onDismiss={() => setBcOpenDialog(d => ({ ...d, visible: false }))}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'BC-Bestellung öffnen',
          subText: 'Möchten Sie die Bestellung im Webbrowser oder in der BC-Client-App öffnen?',
        }}
        minWidth={460}
      >
        <DialogFooter styles={{ actions: { display: 'flex', gap: 8, flexWrap: 'nowrap' } }}>
          <PrimaryButton
            text="Webbrowser"
            iconProps={{ iconName: 'Globe' }}
            styles={{ label: { whiteSpace: 'nowrap' } }}
            onClick={() => {
              window.open(bcOpenDialog.browserUrl, '_blank');
              setBcOpenDialog(d => ({ ...d, visible: false }));
            }}
          />
          <DefaultButton
            text="Client-App"
            iconProps={{ iconName: 'AppIconDefaultList' }}
            styles={{ label: { whiteSpace: 'nowrap' } }}
            onClick={() => {
              window.location.href = bcOpenDialog.clientUrl;
              setBcOpenDialog(d => ({ ...d, visible: false }));
            }}
          />
          <DefaultButton
            text="Abbrechen"
            styles={{ label: { whiteSpace: 'nowrap' } }}
            onClick={() => setBcOpenDialog(d => ({ ...d, visible: false }))}
          />
        </DialogFooter>
      </Dialog>

      {/* Lieferung melden */}
      <Dialog
        hidden={!lieferungDialogVisible}
        onDismiss={() => setLieferungDialogVisible(false)}
        dialogContentProps={{ type: DialogType.normal, title: 'Lieferung melden' }}
        modalProps={{ isBlocking: false }}
        minWidth={520}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Toggle
            label="Gesamte Bestellung geliefert"
            checked={lieferungGesamte}
            onChange={(_, v) => setLieferungGesamte(!!v)}
          />
          {!lieferungGesamte && (
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>
                Positionen auswählen und gelieferte Menge eintragen:
              </Text>
              {(bestellung?.Positionen || []).map((pos) => {
                const state = lieferungPositionen[pos.Id!] || { checked: false, menge: String(pos.Menge || '') };
                return (
                  <Stack key={pos.Id} horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                    <input
                      type="checkbox"
                      checked={state.checked}
                      onChange={(e) =>
                        setLieferungPositionen((prev) => ({
                          ...prev,
                          [pos.Id!]: { ...state, checked: e.target.checked },
                        }))
                      }
                      style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <Text
                      variant="small"
                      styles={{ root: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: state.checked ? '#323130' : '#a19f9d' } }}
                    >
                      Pos. {pos.PosID} – {pos.Artikelname}
                    </Text>
                    <TextField
                      value={state.menge}
                      onChange={(_, v) =>
                        setLieferungPositionen((prev) => ({
                          ...prev,
                          [pos.Id!]: { ...state, menge: v || '' },
                        }))
                      }
                      disabled={!state.checked}
                      suffix={pos.Mengeneinheit || ''}
                      styles={{ root: { width: 110 } }}
                      placeholder="Menge"
                    />
                  </Stack>
                );
              })}
            </Stack>
          )}
          {lieferungGesamte && (
            <Text variant="small" styles={{ root: { color: '#107c10' } }}>
              Alle Positionen werden als vollständig geliefert markiert.
            </Text>
          )}
        </Stack>
        <DialogFooter>
          <PrimaryButton
            text={lieferungSaving ? 'Wird gespeichert…' : 'Speichern'}
            onClick={handleLieferungMelden}
            disabled={lieferungSaving}
          />
          <DefaultButton text="Abbrechen" onClick={() => setLieferungDialogVisible(false)} disabled={lieferungSaving} />
        </DialogFooter>
      </Dialog>

      {/* BC-Sync Ergebnis */}
      <Dialog
        hidden={!bcSyncResultDialog.visible}
        onDismiss={() => setBcSyncResultDialog(d => ({ ...d, visible: false }))}
        dialogContentProps={{
          type: DialogType.normal,
          title: bcSyncResultDialog.success ? 'BC-Bestellung angelegt' : 'BC-Fehler',
        }}
      >
        <Stack tokens={{ childrenGap: 8 }}>
          <MessageBar messageBarType={bcSyncResultDialog.success ? MessageBarType.success : MessageBarType.error}>
            {bcSyncResultDialog.message}
          </MessageBar>
          {bcSyncResultDialog.details && (
            <Text variant="small" styles={{ root: { whiteSpace: 'pre-line', color: '#605e5c' } }}>
              {bcSyncResultDialog.details}
            </Text>
          )}
        </Stack>
        <DialogFooter>
          <DefaultButton onClick={() => setBcSyncResultDialog(d => ({ ...d, visible: false }))} text="Schließen" />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
